import React, { useState, useRef, useCallback, useEffect } from "react";
import * as Tone from "tone";
import CodeMirror from "@uiw/react-codemirror";
import { javascript } from "@codemirror/lang-javascript";
import { oneDark } from "@codemirror/theme-one-dark";
import Header from "./Header";
import { saveLearningProgress, getLearningProgress } from "./database";
import { auth } from "./firebase";
import { onAuthStateChanged } from "firebase/auth";


import { Trophy, Lock, CheckCircle, ChevronRight, BookOpen, Award, Lightbulb, Target, Code } from "lucide-react";

// ============================================================================
// FIREBASE TEMPLATE - Your friend will implement these functions
// ============================================================================


// ============================================================================
// STYLES
// ============================================================================
// ============================================================================
// STYLES
// ============================================================================
// ============================================================================
// STYLES
// ============================================================================
const highlightStyle = document.createElement("style");
highlightStyle.innerHTML = `
  @keyframes flashAnimation {
    0% { background-color: rgba(60, 255, 60, 0.4); border-left-color: #0f0; }
    100% { background-color: transparent; border-left-color: transparent; }
  }

  .active-line {
    animation: flashAnimation 0.15s ease-out forwards;
    will-change: background-color, border-left-color;
    border-left: 4px solid transparent;
    position: relative;
    z-index: 1;
  }

  .lesson-card {
    transition: all 0.2s ease;
  }

  .lesson-card:hover:not(.locked) {
    transform: translateX(3px);
    background-color: rgba(0, 255, 0, 0.08) !important;
  }

  /* --- DESKTOP LAYOUT (Full Width Force) --- */
  .main-layout-container {
    display: flex;
    flex-wrap: nowrap; /* Prevent wrapping */
    width: 100%;
    min-width: 1300px; /* FORCE SCROLL: 300 + 600 + 320 + borders */
    max-width: 1600px;
    height: 85vh; /* DESKTOP: Original height */
    min-height: 600px;
    border: 1px solid #333;
    background-color: #282c34;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 50px 100px rgba(0,0,0,0.9);
  }

  /* MOBILE ONLY: Compact panel height */
  @media screen and (max-width: 768px) {
    .learn-page-wrapper {
      min-height: auto !important;
      height: auto !important;
    }
    .main-layout-container {
      height: auto !important;
      min-height: 500px !important;
      max-height: 70vh !important;
    }
  }

  .sidebar-lessons {
    width: 300px;
    min-width: 300px; /* RIGID WIDTH */
    flex-shrink: 0;
    border-right: 1px solid #111;
    background-color: #050505;
    overflow-y: auto;
    padding: 25px;
  }

  .editor-area {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-width: 600px; /* RIGID WIDTH for Editor */
  }

  .sidebar-controls {
    width: 320px;
    min-width: 320px; /* RIGID WIDTH */
    flex-shrink: 0;
    background-color: #050505;
    padding: 25px;
    display: flex;
    flex-direction: column;
    border-left: 1px solid #222;
    overflow-y: auto;
  }

  /* MEDIA QUERY REMOVED FOR DESKTOP VIEW ON MOBILE */

  /* --- SCROLLBAR AYARLARI --- */
  
  /* Sol taraf için şık scrollbar */
  .custom-scrollbar::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }
  .custom-scrollbar::-webkit-scrollbar-track {
    background: #050505;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: #333;
    border-radius: 10px;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #555;
  }

  /* Sağ taraf için GİZLİ scrollbar */
  .hide-scrollbar {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
  .hide-scrollbar::-webkit-scrollbar {
    display: none;
  }
`;
document.head.appendChild(highlightStyle);

// ============================================================================
// LESSON DATA
// ============================================================================
const LESSONS = {
  beginner: {
    title: "BEGINNER",
    subtitle: "Foundation & Basics",
    color: "#00d8ff",
    icon: "🟢",
    stages: [
      {
        id: "b1",
        title: "Stage 1: First Note",
        description: "Single instrument, single note",
        objective: "Play one note with kick drum",
        starterCode: `// Single kick hit\n{\n  kick(C1)\n}`,
        solution: `{\n  kick(C1)\n}`,
        theory: "In SoundCode, we use curly braces {} to write music code. We write the instrument name inside and specify the notes in parentheses.",
        hints: ["Write code between { and }", "kick() is a bass drum sound", "C1 is a note name"]
      },
      {
        id: "b2",
        title: "Stage 2: Rhythm Basics",
        description: "Multiple notes",
        objective: "Write a simple 4-beat kick rhythm (C1 four times)",
        starterCode: `// 4-beat kick\n{\n  kick()\n}`,
        solution: `{\n  kick(C1, C1, C1, C1)\n}`,
        theory: "Notes are separated by commas. Each comma separates a step (beat). 4 notes = 1 bar (measure).",
        hints: ["Separate notes with commas", "C1, C1, C1, C1", "4 notes = 4 beats"]
      },
      {
        id: "b3",
        title: "Stage 3: Silence",
        description: "Using rest (-)",
        objective: "Kick: hit, rest, hit, rest (C1, -, C1, -)",
        starterCode: `// Kick and silence\n{\n  kick(C1, C1, C1, C1)\n}`,
        solution: `{\n  kick(C1, -, C1, -)\n}`,
        theory: "The - (dash) sign means silence (rest). In music, leaving space is as important as sound.",
        hints: ["- character = silence", "Rhythm: balance of sound and silence", "C1, -, C1, -"]
      },
      {
        id: "b4",
        title: "Stage 4: Second Instrument",
        description: "Kick + Snare together",
        objective: "Use Kick (C1*4) and Snare (-, C4, -, C4)",
        starterCode: `{\n  kick(C1, C1, C1, C1)\n}`,
        solution: `{\n  kick(C1, C1, C1, C1)\n  snare(-, C4, -, C4)\n}`,
        theory: "You can use multiple instruments in one block. Each line represents an instrument.",
        hints: ["Each instrument on new line", "kick and snare play together", "snare usually on 2nd and 4th beat"]
      },
      {
        id: "b5",
        title: "Stage 5: Triple Combination",
        description: "Kick + Snare + Hihat",
        objective: "Kick (C1*4), Snare (-, C4, -, C4), Hihat (C4*4)",
        starterCode: `{\n  kick(C1, C1, C1, C1)\n  snare(-, C4, -, C4)\n}`,
        solution: `{\n  kick(C1*4)\n  snare(-, C4, -, C4)\n  hihat(C4*4)\n}`,
        theory: "Drum kit: Kick (bass), Snare (snare), Hihat (cymbal). This trio is the foundation of rock/pop music.",
        hints: ["3 instruments = full drum", "Each line 4 beats", "Hihat plays continuously"]
      },
      {
        id: "b6",
        title: "Stage 6: Multiplication Operator",
        description: "Repeat notes with *",
        objective: "Hihat subdivision: use [C4, C4]*2",
        starterCode: `{\n  kick(C1, C1, C1, C1)\n  hihat([C4, C4], [C4, C4])\n}`,
        solution: `{\n  kick(C1*4)\n  hihat([C4, C4]*2)\n}`,
        theory: "The * operator repeats the note or group the specified number of times. [C4, C4]*2 means write the group [C4, C4] 2 times.",
        hints: ["Repeat group with []*number", "Shortens code", "Stays within 4-beat bar"]
      },
      {
        id: "b7",
        title: "Stage 7: Subdivision",
        description: "Fast notes with []",
        objective: "Hihat: C4, [C4, C4], C4, -",
        starterCode: `{\n  hihat(C4, C4, C4, C4)\n}`,
        solution: `{\n  hihat(C4, [C4, C4], C4, -)\n}`,
        theory: "Notes inside square brackets [] fit into one beat. [C4, C4] = two fast notes in one beat.",
        hints: ["[] = subdivisions", "Multiple notes in one beat", "Intensifies rhythms"]
      },
      {
        id: "b8",
        title: "Stage 8: Block Repeat",
        description: "Loop with }*number",
        objective: "Repeat a block 2 times (}*2)",
        starterCode: `{\n  kick(C1*4)\n  snare(-, C4, -, C4)\n}`,
        solution: `{\n  kick(C1*4)\n  snare(-, C4, -, C4)\n}*2`,
        theory: "You can repeat a block as many times as you want by adding *number at the end.",
        hints: ["}*2 = play block 2 times", "Creates loop", "Music grows with repetition"]
      },
      {
        id: "b9",
        title: "Stage 9: Multiple Blocks",
        description: "Different blocks in sequence",
        objective: "Write two different blocks (like verse and chorus)",
        starterCode: `{\n  kick(C1*4)\n}*2`,
        solution: `{\n  kick(C1*4)\n  snare(-, C4, -, C4)\n}*2\n\n{\n  kick([C1, C1], C1*2)\n  hihat(C4*8)\n}*2`,
        theory: "Songs consist of different sections. Each block carries different energy and character.",
        hints: ["Leave space between blocks", "Each block can be different", "Verse -> Chorus structure"]
      },
      {
        id: "b10",
        title: "Stage 10: First Melody",
        description: "Melody with bass",
        objective: "Play 4 different notes with bass (E1, E1, G1, A1)",
        starterCode: `{\n  bass(E1, E1, E1, E1)\n}`,
        solution: `{\n  bass(E1, E1, G1, A1)\n}`,
        theory: "Bass note plays low-frequency melodies. There are note names like E, G, A.",
        hints: ["Different notes = melody", "E1, G1, A1 different sounds", "Bass supports rhythm"]
      },
      {
        id: "b11",
        title: "Stage 11: Chord Support",
        description: "Bass + Synth harmony",
        objective: "Bass: E1*4, Synth: E3, G3, B3, E4 (E minor chord)",
        starterCode: `{\n  bass(E1*4)\n}`,
        solution: `{\n  bass(E1*4)\n  synth(E3, G3, B3, E4)\n}`,
        theory: "Chord is notes that sound good together. Bass below, synth above creates harmony.",
        hints: ["E3, G3, B3 = E minor", "Bass and melody should harmonize", "Harmony = playing together"]
      },
      {
        id: "b12",
        title: "Stage 12: Full Song",
        description: "Combine everything",
        objective: "Write a small song with Kick, Snare, Hihat, Bass, Synth",
        starterCode: `{\n  kick(C1*4)\n  snare(-, C4, -, C4)\n}`,
        solution: `{\n  kick(C1*4)\n  snare(-, C4, -, C4)\n  hihat(C4*8)\n  bass(E1, E1, G1, A1)\n  synth(E4, G4, B4, E5)\n}*2`,
        theory: "You've completed the beginner level! Now you can write basic rhythm and melody.",
        hints: ["Combine everything you've learned", "Drums + Bass + Melody", "Create your own style"]
      }
    ]
  },

  intermediate: {
    title: "INTERMEDIATE",
    subtitle: "Effects & Production",
    color: "#c084fc",
    icon: "🟡",
    stages: [
      {
        id: "i1",
        title: "Stage 1: Volume Control",
        description: ".volume() effect",
        objective: "Play Kick at 120, Hihat at 50 volume",
        starterCode: `{\n  kick(C1*4)\n  hihat(C4*8)\n}`,
        solution: `{\n  kick(C1*4).volume(120)\n  hihat(C4*8).volume(50)\n}`,
        theory: ".volume() adjusts the instrument's sound level. 100 normal, 50 half volume, 150 high volume.",
        hints: ["Use .volume(number)", "100 = normal level", "Foundation of mixing"]
      },
      {
        id: "i2",
        title: "Stage 2: Panning",
        description: "Stereo with .pan()",
        objective: "Place Hihat left (-0.7), Snare right (0.7)",
        starterCode: `{\n  hihat([C4, C4]*2)\n  snare(-, C4, -, C4)\n}`,
        solution: `{\n  hihat([C4, C4]*2).pan(-0.7)\n  snare(-, C4, -, C4).pan(0.7)\n}`,
        theory: ".pan() positions sound left-right. -1 = full left, 0 = center, 1 = full right.",
        hints: ["Between -1 and 1", "Creates stereo width", "Left-right balance"]
      },
      {
        id: "i3",
        title: "Stage 3: Gain Effect",
        description: "Precise control with .gain()",
        objective: "Add .gain(1.5) to bass",
        starterCode: `{\n  bass(E1*4)\n}`,
        solution: `{\n  bass(E1*4).gain(1.5)\n}`,
        theory: ".gain() multiplies sound intensity. 1 = normal, 0.5 = half, 2 = double.",
        hints: ["Multiplier effect", "1.5 = 50% increase", "Different from volume"]
      },
      {
        id: "i4",
        title: "Stage 4: Velocity",
        description: "Note strength (velocity)",
        objective: "Use ghost note in snare: C4(1), -, C4(0.3), -",
        starterCode: `{\n  snare(C4, -, C4, -)\n}`,
        solution: `{\n  snare(C4(1), -, C4(0.3), -)\n}`,
        theory: "Number in parentheses sets note strength (velocity). (1) = full strength, (0.3) = light.",
        hints: ["note(strength) format", "Between 0 and 1", "Ghost notes = light notes"]
      },
      {
        id: "i5",
        title: "Stage 5: BPM Control",
        description: "Tempo setting",
        objective: "Set BPM to 140",
        starterCode: `{\n  kick(C1*4)\n}`,
        solution: `bpm = 140\n\n{\n  kick(C1*4)\n}`,
        theory: "BPM (Beats Per Minute) sets song speed. 120 = medium, 140 = fast, 90 = slow.",
        hints: ["bpm = number at code start", "Affects entire block", "Typically 60-200"]
      },
      {
        id: "i6",
        title: "Stage 6: Swing",
        description: "Add groove",
        objective: "Add swing = 0.3",
        starterCode: `bpm = 120\n\n{\n  kick(C1*4)\n  hihat([C4, C4]*2)\n}`,
        solution: `bpm = 120\nswing = 0.3\n\n{\n  kick(C1*4)\n  hihat([C4, C4]*2)\n}`,
        theory: "Swing adds natural groove to rhythms. 0 = straight, 0.5 = maximum swing.",
        hints: ["Between 0 and 1", "Adds naturalness", "Jazz/funk feel"]
      },
      {
        id: "i7",
        title: "Stage 7: Master Volume",
        description: "Overall sound level",
        objective: "Set master_volume = 85",
        starterCode: `bpm = 120\n\n{\n  kick(C1*4)\n}`,
        solution: `bpm = 120\nmaster_volume = 85\n\n{\n  kick(C1*4)\n}`,
        theory: "master_volume affects all sounds. Provides global control.",
        hints: ["Between 0-100", "Affects all instruments", "Final mixdown"]
      },
      {
        id: "i8",
        title: "Stage 8: Advanced Subdivision",
        description: "Nested division [[]]",
        objective: "Hihat: [C4, [C4, C4]]",
        starterCode: `{\n  hihat([C4, C4])\n}`,
        solution: `{\n  hihat([C4, [C4, C4]])\n}`,
        theory: "Nested brackets create more complex rhythms. Used a lot in drill/trap music.",
        hints: ["[[]] = nested", "Very fast rhythms", "Detailed control"]
      },
      {
        id: "i9",
        title: "Stage 9: Reverb",
        description: "Echo effect",
        objective: "Add .reverb(0.6) to snare",
        starterCode: `{\n  snare(-, C4, -, C4)\n}`,
        solution: `{\n  snare(-, C4, -, C4).reverb(0.6)\n}`,
        theory: ".reverb() adds echo. Creates enclosed space feel. 0 = none, 1 = maximum.",
        hints: ["Between 0-1", "Space perception", "Creates atmosphere"]
      },
      {
        id: "i10",
        title: "Stage 10: Delay",
        description: "Echo effect",
        objective: "Add .delay(0.4) to hihat",
        starterCode: `{\n  hihat([C4, C4]*2)\n}`,
        solution: `{\n  hihat([C4, C4]*2).delay(0.4)\n}`,
        theory: ".delay() creates echo effect. Adds rhythmic repetition.",
        hints: ["Echo = delay", "Between 0-1", "Creates rhythm"]
      },
      {
        id: "i11",
        title: "Stage 11: Chorus",
        description: "Widening effect",
        objective: "Add .chorus(1, 2.5, 0.5) to bass",
        starterCode: `{\n  bass(E1*4)\n}`,
        solution: `{\n  bass(E1*4).chorus(1, 2.5, 0.5)\n}`,
        theory: ".chorus() widens and enriches sound. Gives thicker sound.",
        hints: ["Takes 3 parameters", "Widens sound", "Adds richness"]
      },
      {
        id: "i12",
        title: "Stage 12: EQ (Equalizer)",
        description: "Frequency adjustment",
        objective: "Add .eq(5, 0, -15) to kick (boost bass, cut treble)",
        starterCode: `{\n  kick(C1*4)\n}`,
        solution: `{\n  kick(C1*4).eq(5, 0, -15)\n}`,
        theory: ".eq(low, mid, high) adjusts frequencies. +value increases, -value decreases.",
        hints: ["low, mid, high", "+/- values", "Frequency shaping"]
      },
      {
        id: "i13",
        title: "Stage 13: Distortion",
        description: "Distortion effect",
        objective: "Add .distortion(0.6) to kick",
        starterCode: `{\n  kick(C1*4)\n}`,
        solution: `{\n  kick(C1*4).distortion(0.6)\n}`,
        theory: ".distortion() distorts and hardens sound. Ideal for rock/metal.",
        hints: ["Sound hardens", "Between 0-1", "Aggressive tone"]
      },
      {
        id: "i14",
        title: "Stage 14: Bitcrusher",
        description: "8-bit effect",
        objective: "Add .bitcrusher(4) to synth",
        starterCode: `{\n  synth(C4, E4, G4)\n}`,
        solution: `{\n  synth(C4, E4, G4).bitcrusher(4)\n}`,
        theory: ".bitcrusher() gives retro game sound. As bits decrease, sound 'pixelates'.",
        hints: ["Retro sound", "Between 1-16 bits", "8-bit = classic"]
      },
      {
        id: "i15",
        title: "Stage 15: Effect Chaining",
        description: "Multiple effects",
        objective: "Snare: .distortion(0.4).reverb(0.5).pan(0.3)",
        starterCode: `{\n  snare(-, C4, -, C4)\n}`,
        solution: `{\n  snare(-, C4, -, C4).distortion(0.4).reverb(0.5).pan(0.3)\n}`,
        theory: "Effects chain in sequence. Order is important!",
        hints: ["Effects in sequence", "Order matters", "Create chain"]
      },
      {
        id: "i16",
        title: "Stage 16: Production Mix",
        description: "Full production",
        objective: "Create balanced mix using all effects",
        starterCode: `bpm = 130\n\n{\n  kick(C1*4)\n  snare(-, C4, -, C4)\n  hihat([C4, C4]*2)\n  bass(E1*4)\n}`,
        solution: `bpm = 130\nswing = 0.2\nmaster_volume = 90\n\n{\n  kick(C1*4).volume(110).eq(4, 0, -10)\n  snare(-, C4, -, C4).reverb(0.3).pan(0.2)\n  hihat([C4, C4]*2).volume(60).pan(-0.4)\n  bass(E1*4).chorus(1, 2.5, 0.4).eq(2, -3, -8)\n}*2`,
        theory: "Professional mixing: volume balance + panning + effects + EQ.",
        hints: ["Balance each instrument", "Use stereo space", "Separate with EQ"]
      }
    ]
  },

  expert: {
    title: "EXPERT",
    subtitle: "Advanced Composition",
    color: "#fb923c",
    icon: "🔴",
    stages: [
      {
        id: "e1",
        title: "Stage 1: Polyrhythm Basics",
        description: "Different loop lengths",
        objective: "Kick 4 beats, Synth 3 beats in loop",
        starterCode: `{\n  kick(C1*4)\n  synth(C4, E4, G4)\n}`,
        solution: `{\n  kick(C1*4)\n}*3\n\n{\n  synth(C4, E4, G4, -)\n}*4`,
        theory: "Polyrhythm: different length loops simultaneously. 3*4 = 4*3 = 12 beats to align.",
        hints: ["Different block lengths", "Mathematical rhythm", "3 and 4 harmony"]
      },
      {
        id: "e2",
        title: "Stage 2: Nested Subdivisions",
        description: "Nested divisions",
        objective: "Complex hihat pattern: [C4, [C4, C4]]",
        starterCode: `{\n  hihat([C4, C4]*2)\n}`,
        solution: `{\n  hihat([C4, [C4, C4]], [C4, C4])\n}`,
        theory: "Nested subdivisions: subdivision within subdivision. Used in drill/trap music.",
        hints: ["[[]] = nested", "Very fast rhythms", "Complex pattern"]
      },
      {
        id: "e3",
        title: "Stage 3: Chained Effects",
        description: "Effect Chaining Order",
        objective: "Distortion first then Reverb",
        starterCode: `{\n  snare(-, C4, -, C4)\n}`,
        solution: `{\n  snare(-, C4, -, C4).distortion(0.8).reverb(0.5)\n}`,
        theory: "Effect order dramatically changes sound. Signal flow is fundamental to sound design.",
        hints: ["Effect order important!", "Distortion->Reverb", "Different result"]
      },
      {
        id: "e4",
        title: "Stage 4: Chord Progressions",
        description: "Chord progression",
        objective: "Write Am - F - C - G progression",
        starterCode: `{\n  synth(A3, C4, E4)\n}`,
        solution: `{\n  synth(A3, C4, E4)\n}\n\n{\n  synth(F3, A3, C4)\n}\n\n{\n  synth(C3, E3, G3)\n}\n\n{\n  synth(G3, B3, D4)\n}`,
        theory: "Chord progression: chords in sequence. Am-F-C-G is a popular progression.",
        hints: ["Each block one chord", "4 chords in sequence", "Am, F, C, G"]
      },
      {
        id: "e5",
        title: "Stage 5: Bass Walking",
        description: "Bass walking",
        objective: "Chromatic bass line: E1, F1, F#1, G1, G#1, A1",
        starterCode: `{\n  bass(E1*4)\n}`,
        solution: `{\n  bass(E1, F1, F#1, G1, G#1, A1, G#1, G1)\n}`,
        theory: "Walking bass: bass walking in half steps. Foundation of jazz.",
        hints: ["Half step steps", "# = sharp", "Smooth transition"]
      },
      {
        id: "e6",
        title: "Stage 6: Countermelody",
        description: "Counter melody",
        objective: "Main melody (synth) + counter melody (bass) harmonious",
        starterCode: `{\n  synth(C4, E4, G4, C5)\n  bass(C2*4)\n}`,
        solution: `{\n  synth(C4, E4, G4, C5)\n  bass(C2, G1, E1, C1)\n}*2`,
        theory: "Countermelody: second melody accompanying main melody. Harmonious but different.",
        hints: ["Two melodies together", "Must be harmonious", "Different rhythm"]
      },
      {
        id: "e7",
        title: "Stage 7: Dynamic Velocity",
        description: "Velocity variation",
        objective: "Bass: create strong-light pattern",
        starterCode: `{\n  bass(E1*4)\n}`,
        solution: `{\n  bass(E1(1), E1(0.3), E1(1), E1(0.3))\n}`,
        theory: "Velocity variation: changes note strength to create dynamics. (1) = strong, (0.3) = light.",
        hints: ["Dynamics with velocity", "Strong-light pattern", "Adds liveliness"]
      },
      {
        id: "e8",
        title: "Stage 8: Stereo Widening",
        description: "Stereo widening",
        objective: "Widen synth with .widener(0.8)",
        starterCode: `{\n  synth(C4, E4, G4, C5)\n}*4`,
        solution: `{\n  synth(C4, E4, G4, C5).widener(0.8)\n}*4`,
        theory: "Widener: expands stereo field. Sound feels bigger, fuller.",
        hints: ["Stereo width", "Between 0-1", "Adds fullness"]
      },
      {
        id: "e9",
        title: "Stage 9: Intro Design",
        description: "Minimal start",
        objective: "Slow starting intro adding instruments gradually",
        starterCode: `bpm = 120\n\n{\n  \n}*4`,
        solution: `bpm = 110\n\n{\n  synth(E4, -, -, -).reverb(0.8)\n}*2\n\n{\n  synth(E4, G4, -, -).reverb(0.8)\n  bass(E1, -, -, -)\n}*2`,
        theory: "Intro: introduces song. Starts with few instruments, builds gradually.",
        hints: ["Start minimal", "Add gradually", "Create atmosphere"]
      },
      {
        id: "e10",
        title: "Stage 10: Build-up",
        description: "Tension building",
        objective: "Write build-up creating tension (energy increase)",
        starterCode: `{\n  kick(C1*4)\n}*2`,
        solution: `{\n  kick(C1*4)\n  hihat([C4, C4]*2).volume(80)\n}*1\n\n{\n  kick(C1*4)\n  snare(-, -, -, [C4, C4])\n  hihat([C4, C4]*2).volume(100)\n}*1\n\n{\n  kick([C1, C1]*2)\n  snare(-, C4, -, [C4, C4, C4])\n  hihat([[C4, C4], C4]*2).volume(120)\n}*1`,
        theory: "Build-up: tension before drop. Increasing intensity, speed, effects.",
        hints: ["Gradual increase", "Rhythm intensifies", "Effects increase"]
      },
      {
        id: "e11",
        title: "Stage 11: Drop Design",
        description: "Energy explosion",
        objective: "Design powerful drop",
        starterCode: `{\n  kick(C1*4)\n}`,
        solution: `{\n  kick(C1*4).volume(130).distortion(0.5).eq(6, 0, -15)\n  snare(-, C4(1), -, C4(0.4)).pan(0.4)\n  hihat([C4, [C4, C4]]*2).volume(90).pan(-0.5)\n  bass(E1, [E1, E1], E1, -).chorus(1, 3, 0.6).eq(4, -1, -12)\n}*4`,
        theory: "Drop: highest energy point. All instruments strong, effects maximum.",
        hints: ["Maximum energy", "Strong distortion", "All instruments active"]
      },
      {
        id: "e12",
        title: "Stage 12: Full Production",
        description: "Complete song structure",
        objective: "Intro → Verse → Chorus → Drop → Outro",
        starterCode: `bpm = 128\n\n// Buraya şarkını yaz`,
        solution: `bpm = 128\nswing = 0.15\nmaster_volume = 88\n\n// INTRO\n{\n  synth(E4, G4, B4, -).reverb(0.7).volume(70)\n  bass(E1, -, -, -).chorus(1, 2, 0.3)\n}*2\n\n// VERSE\n{\n  kick(C1*4).eq(5, 0, -12)\n  snare(-, C4, -, C4).pan(0.2)\n  hihat([C4, C4]*2).volume(60).pan(-0.3)\n  bass(E1, E1, G1, A1).eq(3, -2, -10)\n}*2\n\n// CHORUS\n{\n  kick(C1*4).volume(120).distortion(0.3)\n  snare(-, C4, -, C4).reverb(0.4)\n  hihat([[C4, C4]]*4).volume(80)\n  bass(E1, [E1, E1], G1, -).chorus(1, 2.5, 0.5)\n  synth(E4, G4, B4, E5).widener(0.7)\n}*4\n\n// DROP\n{\n  kick(C1*4).volume(130).distortion(0.5).eq(6, 0, -15)\n  snare(-, C4(1), -, C4(0.4)).pan(0.4)\n  hihat([C4, [C4, C4]]*2).volume(90).pan(-0.5)\n  bass(E1, [E1, E1], E1, -).chorus(1, 3, 0.6).eq(4, -1, -12)\n  synth(E4, G4, B4, E5).distortion(0.2).widener(0.8)\n}*4\n\n// OUTRO\n{\n  bass(E1, -, -, -).reverb(0.9)\n  synth(E4, G4, -, -).reverb(0.9).volume(50)\n}*2`,
        theory: "Full song structure: Each section different energy level, dynamic changes, strategic effect use.",
        hints: ["Each section different energy", "Dynamic changes", "Combine all techniques"]
      }
    ]
  }
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
function LearnPage() {
  const [user, setUser] = useState(null);
  const [currentLevel, setCurrentLevel] = useState("beginner");
  const [currentStageIndex, setCurrentStageIndex] = useState(0);
  // --- LOGIN MODAL STATE ---
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);

  // AUTH CHECK
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        // Delay slightly to avoid flash
        setTimeout(() => setShowLoginModal(true), 1000);
      } else {
        setShowLoginModal(false);
      }
      setAuthLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const [code, setCode] = useState("");
  const [log, setLog] = useState("> Ready to learn. Select a lesson to begin.");
  const [isPlaying, setIsPlaying] = useState(false);
  const [showHints, setShowHints] = useState(false);
  const [showTheory, setShowTheory] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [showSkipWarning, setShowSkipWarning] = useState(false);
  const [codeIsCorrect, setCodeIsCorrect] = useState(false);

  // Mobile detection - uses screen.width which is actual device width, not viewport
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // Check if device is mobile using actual screen width
    const checkMobile = () => {
      const isDeviceMobile = window.screen.width <= 768 ||
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      setIsMobile(isDeviceMobile);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const [progress, setProgress] = useState({
    beginner: Array(12).fill(false),
    intermediate: Array(16).fill(false),
    expert: Array(12).fill(false)
  });

  const editorRef = useRef(null);
  const analyser = useRef(null);
  const instruments = useRef(null);
  const masterReverb = useRef(null);
  const masterDelay = useRef(null);
  const masterChorus = useRef(null);
  const activeEffects = useRef([]);
  const allParts = useRef([]);
  const lineTimeouts = useRef(new Map());
  const lastFlashTime = useRef(new Map());

  const currentStage = LESSONS[currentLevel].stages[currentStageIndex];


  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);

      if (currentUser) {
        console.log("✅ User logged in:", currentUser.uid);
        getLearningProgress(currentUser.uid).then(loadedProgress => {
          console.log("📖 Progress loaded:", loadedProgress);
          setProgress(loadedProgress);
        });
      } else {
        console.log("❌ No user logged in");
        setProgress({
          beginner: Array(12).fill(false),
          intermediate: Array(16).fill(false),
          expert: Array(12).fill(false)
        });
      }
    });

    return () => unsubscribe();
  }, []);


  useEffect(() => {
    analyser.current = new Tone.Analyser("fft", 128);
    analyser.current.toDestination();

    masterReverb.current = new Tone.Reverb({
      decay: 2.5,
      preDelay: 0.01
    }).connect(analyser.current);
    masterReverb.current.generate();

    masterDelay.current = new Tone.FeedbackDelay("8n", 0.4).connect(analyser.current);
    masterChorus.current = new Tone.Chorus(4, 2.5, 0.5).connect(analyser.current).start();

    instruments.current = {
      kick: new Tone.PolySynth(Tone.MembraneSynth, { volume: -8 }).connect(analyser.current),
      snare: new Tone.NoiseSynth({
        envelope: { attack: 0.005, decay: 0.1, sustain: 0 },
        volume: -2
      }).connect(analyser.current),
      hihat: new Tone.MetalSynth({
        frequency: 200,
        envelope: { attack: 0.001, decay: 0.1, release: 0.01 },
        harmonicity: 5.1, modulationIndex: 32,
        resonance: 4000, octaves: 1.5,
        volume: -12
      }).connect(analyser.current),
      bass: new Tone.PolySynth(Tone.FMSynth, {
        harmonicity: 1, modulationIndex: 3.5,
        oscillator: { type: "custom", partials: [0, 1, 0, 2] },
        envelope: { attack: 0.08, decay: 0.3, sustain: 0, release: 0.2 },
        modulation: { type: "square" },
        modulationEnvelope: { attack: 0.1, decay: 0.2, sustain: 0.3, release: 0.01 },
        volume: -5
      }).connect(analyser.current),
      synth: new Tone.PolySynth(Tone.AMSynth, { volume: -5 }).connect(analyser.current),
      default: new Tone.PolySynth(Tone.Synth, { volume: -10 }).connect(analyser.current)
    };


    Tone.loaded().then(() => {
      setIsLoaded(true);
    });

    return () => {
      if (instruments.current) {
        Object.values(instruments.current).forEach(inst => inst.dispose());
      }
      masterReverb.current?.dispose();
      masterDelay.current?.dispose();
      masterChorus.current?.dispose();
      analyser.current?.dispose();
      lineTimeouts.current.forEach(timeoutId => clearTimeout(timeoutId));
      lineTimeouts.current.clear();
    };
  }, [user]);

  useEffect(() => {
    setCode(currentStage.starterCode);
    setLog("> Lesson loaded. Read the objective and start coding!");
    setShowHints(false);
    setShowTheory(false);
    setCodeIsCorrect(false);
    stopMusic();
  }, [currentLevel, currentStageIndex]);

  const onCreateEditor = useCallback((view) => {
    editorRef.current = view;
  }, []);

  const flashLine = (lineIndex, time) => {
    Tone.Draw.schedule(() => {
      if (!editorRef.current || !editorRef.current.contentDOM) return;

      const lines = editorRef.current.contentDOM.querySelectorAll(".cm-line");
      const lineElement = lines[lineIndex];

      if (lineElement) {
        lineElement.classList.remove("active-line");
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            lineElement.classList.add("active-line");
          });
        });
      }
    }, time);
  };

  const validateCode = (userCode, solutionCode) => {
    const normalize = (str) => {
      return str
        .replace(/\/\/.*/g, '')
        .replace(/\s+/g, '')
        .toLowerCase();
    };

    const normalizedUser = normalize(userCode);
    const normalizedSolution = normalize(solutionCode);

    if (normalizedUser === normalizedSolution) {
      return true;
    }

    const similarity = calculateSimilarity(normalizedUser, normalizedSolution);
    return similarity >= 0.8;
  };

  const calculateSimilarity = (str1, str2) => {
    const longer = str1.length > str2.length ? str1 : str2;
    const shorter = str1.length > str2.length ? str2 : str1;

    if (longer.length === 0) return 1.0;

    const editDistance = levenshteinDistance(longer, shorter);
    return (longer.length - editDistance) / longer.length;
  };

  const levenshteinDistance = (str1, str2) => {
    const matrix = [];

    for (let i = 0; i <= str2.length; i++) {
      matrix[i] = [i];
    }

    for (let j = 0; j <= str1.length; j++) {
      matrix[0][j] = j;
    }

    for (let i = 1; i <= str2.length; i++) {
      for (let j = 1; j <= str1.length; j++) {
        if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1
          );
        }
      }
    }

    return matrix[str2.length][str1.length];
  };

  const parseNotes = (notesString) => {
    const result = [];
    let buffer = "";
    let inBracket = false;

    const processToken = (token) => {
      let t = token.trim();
      if (!t) return null;
      if (t.startsWith("|")) return ["-", t.substring(1)];
      else if (t.endsWith("|")) return [t.slice(0, -1), "-"];
      else if (t === "-") return "-";

      const asteriskPos = t.indexOf("*");
      if (asteriskPos !== -1 && !t.includes("(")) {
        const beforeAsterisk = t.substring(0, asteriskPos);
        const afterAsterisk = t.substring(asteriskPos + 1);
        if (Number.isInteger(parseFloat(afterAsterisk))) {
          const note = beforeAsterisk.trim();
          const count = parseInt(afterAsterisk) || 1;
          if (count > 1) return Array(count).fill(note);
          return note;
        }
      }
      return t;
    };

    for (let i = 0; i < notesString.length; i++) {
      const char = notesString[i];
      if (char === "[") { inBracket = true; buffer = ""; }
      else if (char === "]") {
        inBracket = false;
        const subNotesRaw = buffer.split(",");
        const subNotesProcessed = subNotesRaw.map(n => {
          const processed = processToken(n);
          return processed || n.trim();
        });
        result.push(subNotesProcessed);
        buffer = "";
      } else if (char === "," && !inBracket) {
        const processed = processToken(buffer);
        if (processed) result.push(processed);
        buffer = "";
      } else {
        buffer += char;
      }
    }
    const finalToken = processToken(buffer);
    if (finalToken) result.push(finalToken);
    return result;
  };

  const parseNoteDetails = (rawString) => {
    if (typeof rawString !== "string") return { notes: [rawString], velocity: 1 };
    let str = rawString;
    let velocity = 1;

    const parenMatch = str.match(/^(.+?)\(([0-9.]+)\)$/);
    if (parenMatch) {
      str = parenMatch[1];
      const velValue = parseFloat(parenMatch[2]);
      velocity = velValue > 1 ? velValue / 100 : velValue;
    }

    if (str.includes("*")) {
      const parts = str.split("*");
      const potentialVel = parseFloat(parts[1]);
      if (!Number.isInteger(potentialVel) && potentialVel <= 1) {
        str = parts[0];
        velocity = potentialVel;
      }
    }

    const rawNotes = str.split(/\+(?=[A-G])/);
    return { notes: rawNotes, velocity };
  };

  const parseEffectArgs = (argsStr) => {
    if (!argsStr) return [];
    return argsStr.split(",").map(s => {
      const trimmed = s.trim();
      return isNaN(parseFloat(trimmed)) ? trimmed : parseFloat(trimmed);
    });
  };

  const createEffect = (fxName, argsStr) => {
    try {
      const args = parseEffectArgs(argsStr);
      let effectNode = null;

      switch (fxName.toLowerCase()) {
        case "volume":
        case "vol":
          const volVal = args[0] !== undefined ? args[0] : 100;
          return new Tone.Gain(Math.max(0, volVal) / 100);
        case "gain":
          const gainVal = args[0] !== undefined ? args[0] : 1;
          return new Tone.Gain(gainVal);
        case "pan":
        case "panner":
          const panVal = args[0] !== undefined ? args[0] : 0;
          return new Tone.Panner(panVal);
        case "eq":
        case "eq3":
          const low = args[0] !== undefined ? args[0] : 0;
          const mid = args[1] !== undefined ? args[1] : 0;
          const high = args[2] !== undefined ? args[2] : 0;
          return new Tone.EQ3(low, mid, high);
        case "reverb":
          const reverbWet = args[0] !== undefined ? args[0] : 1;
          const reverbSend = new Tone.Gain(reverbWet);
          reverbSend.connect(masterReverb.current);
          return reverbSend;
        case "delay":
        case "feedbackdelay":
          const delayWet = args[2] !== undefined ? args[2] : 1;
          const delaySend = new Tone.Gain(delayWet);
          delaySend.connect(masterDelay.current);
          return delaySend;
        case "chorus":
          const chorusWet = args[3] !== undefined ? args[3] : 1;
          const chorusSend = new Tone.Gain(chorusWet);
          chorusSend.connect(masterChorus.current);
          return chorusSend;
        case "distortion":
          effectNode = new Tone.Distortion(args[0] || 0.4);
          effectNode.wet.value = args[1] !== undefined ? args[1] : 1;
          return effectNode;
        case "bitcrusher":
          effectNode = new Tone.BitCrusher(args[0] || 4);
          effectNode.wet.value = args[1] !== undefined ? args[1] : 1;
          return effectNode;
        case "widener":
          const width = args[0] !== undefined ? args[0] : 0.5;
          effectNode = new Tone.StereoWidener(width);
          return effectNode;
        default:
          return null;
      }
    } catch (e) {
      console.error("Effect error:", e);
      return null;
    }
  };

  const parseBlocks = (codeString) => {
    const blocks = [];
    const lines = codeString.split("\n");
    let currentBlock = null;

    lines.forEach((line, index) => {
      const trimmed = line.trim();
      if (trimmed === "{") {
        currentBlock = { lines: [], loopCount: 1, startLine: index, endLine: -1 };
      } else if (trimmed.startsWith("}")) {
        if (currentBlock) {
          currentBlock.endLine = index;
          const loopMatch = trimmed.match(/\}\s*\*\s*(\d+)/);
          if (loopMatch) currentBlock.loopCount = parseInt(loopMatch[1]);
          blocks.push(currentBlock);
          currentBlock = null;
        }
      } else if (currentBlock && trimmed && !trimmed.startsWith("//")) {
        currentBlock.lines.push({ content: trimmed, lineIndex: index });
      }
    });
    return blocks;
  };

  const parseGlobalSettings = (codeString) => {
    const settings = { bpm: 120, swing: 0, master_volume: 100 };
    const lines = codeString.split("\n");

    lines.forEach(line => {
      const trimmed = line.trim();
      const bpmMatch = trimmed.match(/^bpm\s*=\s*(\d+)/i);
      if (bpmMatch) settings.bpm = parseInt(bpmMatch[1]);
      const swingMatch = trimmed.match(/^swing\s*=\s*([0-9.]+)/i);
      if (swingMatch) settings.swing = parseFloat(swingMatch[1]);
      const volMatch = trimmed.match(/^(master_)?volume\s*=\s*(\d+)/i);
      if (volMatch) settings.master_volume = parseInt(volMatch[2]);
    });
    return settings;
  };

  const runCode = async () => {
    try {
      const isValid = validateCode(code, currentStage.solution);
      setCodeIsCorrect(isValid);

      if (isValid) {
        setLog("✅ Correct! Your code matches the solution!");
      } else {
        setLog("⚠️ Code is running but doesn't match the exact solution. Keep trying!");
      }

      if (Tone.context.state !== "running") await Tone.context.resume();
      await Tone.start();

      Tone.Transport.stop();
      Tone.Transport.cancel(0);

      allParts.current.forEach(part => part.dispose());
      allParts.current = [];

      activeEffects.current.forEach(fx => fx.dispose());
      activeEffects.current = [];

      Object.values(instruments.current).forEach(inst => {
        inst.disconnect();
        inst.connect(analyser.current);
      });

      const settings = parseGlobalSettings(code);
      Tone.Transport.bpm.value = settings.bpm;
      Tone.Transport.swing = settings.swing;
      Tone.Transport.swingSubdivision = "16n";

      const volRatio = Math.max(0, settings.master_volume) / 100;
      const dbVal = (volRatio === 0) ? -Infinity : 20 * Math.log10(volRatio);
      Tone.Destination.volume.rampTo(dbVal, 0.1);

      const blocks = parseBlocks(code);

      if (blocks.length === 0) {
        setLog("❌ No blocks found! Use { } to create blocks.");
        return;
      }

      let currentBarPosition = 0;

      blocks.forEach((block) => {
        const blockEffects = [];
        const allBlockEvents = [];

        block.lines.forEach(({ content, lineIndex }) => {
          const firstParenIndex = content.indexOf("(");
          if (firstParenIndex === -1) return;

          const instrumentName = content.substring(0, firstParenIndex).trim().toLowerCase();
          const rest = content.substring(firstParenIndex);

          let openCount = 0;
          let closeIndex = -1;
          for (let i = 0; i < rest.length; i++) {
            if (rest[i] === "(") openCount++;
            if (rest[i] === ")") openCount--;
            if (openCount === 0) {
              closeIndex = i;
              break;
            }
          }

          const notesRaw = rest.substring(1, closeIndex);
          const chainString = rest.substring(closeIndex + 1);
          const notes = parseNotes(notesRaw);
          const baseSynth = instruments.current[instrumentName] || instruments.current["default"];

          let finalNode = analyser.current;
          const insertChain = [];
          const sendEffects = [];

          if (chainString && chainString.includes(".")) {
            const effectsRaw = [];
            let currentEffect = "";
            let parenDepth = 0;

            for (let i = 0; i < chainString.length; i++) {
              const char = chainString[i];
              if (char === "(") parenDepth++;
              else if (char === ")") parenDepth--;
              else if (char === "." && parenDepth === 0) {
                if (currentEffect.trim()) effectsRaw.push(currentEffect.trim());
                currentEffect = "";
                continue;
              }
              currentEffect += char;
            }
            if (currentEffect.trim()) effectsRaw.push(currentEffect.trim());

            effectsRaw.forEach((fxStr) => {
              const trimmed = fxStr.trim();
              const openParen = trimmed.indexOf("(");
              let fxName, fxArgs;
              if (openParen === -1) {
                fxName = trimmed;
                fxArgs = "";
              } else {
                fxName = trimmed.substring(0, openParen);
                const closeParen = trimmed.lastIndexOf(")");
                fxArgs = trimmed.substring(openParen + 1, closeParen);
              }

              const effectNode = createEffect(fxName, fxArgs);
              if (effectNode) {
                if (['reverb', 'delay', 'feedbackdelay', 'chorus'].includes(fxName.toLowerCase())) {
                  sendEffects.push(effectNode);
                } else {
                  insertChain.push(effectNode);
                }
                blockEffects.push(effectNode);
              }
            });

            if (insertChain.length > 0) {
              for (let i = 0; i < insertChain.length - 1; i++) {
                insertChain[i].connect(insertChain[i + 1]);
              }
              insertChain[insertChain.length - 1].connect(analyser.current);
              finalNode = insertChain[0];
              sendEffects.forEach(sendNode => insertChain[insertChain.length - 1].connect(sendNode));
            } else {
              finalNode = analyser.current;
              if (sendEffects.length > 0) finalNode = { type: 'parallel_sends', sends: sendEffects };
            }
          }

          const barLength = Tone.Time("1m").toSeconds();
          const stepLength = Tone.Time("4n").toSeconds();

          for (let loopIdx = 0; loopIdx < block.loopCount; loopIdx++) {
            notes.forEach((rawStep, stepIdx) => {
              const barOffset = currentBarPosition + loopIdx;
              const stepSeconds = stepIdx * stepLength;
              const absoluteTime = (barOffset * barLength) + stepSeconds;

              allBlockEvents.push({
                time: absoluteTime,
                note: rawStep,
                synth: baseSynth,
                instrumentName: instrumentName,
                outputNode: finalNode,
                lineIndex: lineIndex
              });
            });
          }
        });

        const blockEndTime = (currentBarPosition + block.loopCount) * Tone.Time("1m").toSeconds();
        Tone.Transport.schedule(() => {
          blockEffects.forEach(fx => {
            try { fx.disconnect(); fx.dispose(); } catch (e) { }
          });
        }, blockEndTime);

        if (allBlockEvents.length > 0) {
          allBlockEvents.sort((a, b) => a.time - b.time);

          const blockPart = new Tone.Part((time, event) => {
            const rawStep = event.note;

            if (event.lineIndex !== undefined) {
              flashLine(event.lineIndex, time);
            }

            if (rawStep !== "-") {
              try { event.synth.disconnect(); } catch (e) { }

              if (event.outputNode && event.outputNode.type === 'parallel_sends') {
                event.synth.connect(analyser.current);
                event.outputNode.sends.forEach(sendNode => event.synth.connect(sendNode));
              } else {
                event.synth.connect(event.outputNode);
              }

              if (Array.isArray(rawStep)) {
                const subDuration = Tone.Time("4n").toSeconds() / rawStep.length;
                rawStep.forEach((subNoteRaw, i) => {
                  if (Array.isArray(subNoteRaw)) {
                    const microDuration = subDuration / 2;
                    subNoteRaw.forEach((microNote, k) => {
                      if (microNote !== "-") {
                        const { notes, velocity } = parseNoteDetails(microNote);
                        const triggerTime = time + (i * subDuration) + (k * microDuration);
                        notes.forEach(n => {
                          if (event.instrumentName === "snare") {
                            event.synth.triggerAttackRelease("32n", triggerTime, velocity);
                          } else {
                            event.synth.triggerAttackRelease(n, microDuration, triggerTime, velocity);
                          }
                        });
                      }
                    });
                  } else if (subNoteRaw !== "-") {
                    const { notes, velocity } = parseNoteDetails(subNoteRaw);
                    const triggerTime = time + (i * subDuration);
                    notes.forEach(n => {
                      if (event.instrumentName === "snare") {
                        event.synth.triggerAttackRelease("16n", triggerTime, velocity);
                      } else {
                        event.synth.triggerAttackRelease(n, subDuration, triggerTime, velocity);
                      }
                    });
                  }
                });
              } else {
                const { notes, velocity } = parseNoteDetails(rawStep);
                notes.forEach(n => {
                  if (event.instrumentName === "snare") {
                    event.synth.triggerAttackRelease("8n", time, velocity);
                  } else {
                    event.synth.triggerAttackRelease(n, "8n", time, velocity);
                  }
                });
              }
            }
          }, allBlockEvents).start(0);

          allParts.current.push(blockPart);
        }

        currentBarPosition += block.loopCount;
      });

      const totalBars = currentBarPosition;
      const endTime = totalBars * Tone.Time("1m").toSeconds();
      Tone.Transport.schedule(() => {
        stopMusic();
        setLog("✅ Playback finished!");
      }, endTime);

      Tone.Transport.start("+0.1");
      setIsPlaying(true);
      setLog(`▶️ Playing... BPM: ${settings.bpm}`);

    } catch (error) {
      setLog("❌ ERROR: " + error.message);
      console.error(error);
      stopMusic();
    }
  };

  const stopMusic = () => {
    Tone.Transport.stop();
    Tone.Transport.cancel();

    allParts.current.forEach(part => part.dispose());
    allParts.current = [];

    if (instruments.current) {
      Object.values(instruments.current).forEach(synth => {
        if (synth.releaseAll) synth.releaseAll();
      });
    }

    activeEffects.current.forEach(fx => fx.dispose());
    activeEffects.current = [];

    setIsPlaying(false);
  };

  const isStageUnlocked = (level, index) => {
    if (index === 0) return true;
    return progress[level][index - 1];
  };

  const isLevelUnlocked = (level) => {
    if (level === "beginner") return true;
    if (level === "intermediate") {
      return progress.beginner.every(stage => stage === true);
    }
    if (level === "expert") {
      return progress.intermediate.every(stage => stage === true);
    }
    return false;
  };

  const completeStage = async () => {
    if (!user) {
      setLog("❌ Please login to save progress!");
      return;
    }
    if (progress[currentLevel][currentStageIndex]) {
      setLog("⚠️ This stage is already completed!");
      return;
    }

    const newProgress = { ...progress };
    newProgress[currentLevel][currentStageIndex] = true;
    setProgress(newProgress);

    let xpGained = 0;
    if (currentLevel === "beginner") xpGained = 20;
    else if (currentLevel === "intermediate") xpGained = 50;
    else if (currentLevel === "expert") xpGained = 100;

    try {
      // Firebase'e kaydet ve XP kazandır
      const result = await saveLearningProgress(
        user.uid,
        currentLevel,
        currentStageIndex,
        xpGained
      );

      if (result) {
        setLog(`🎉 Stage completed! +${xpGained} XP earned!`);
      } else {
        setLog("⚠️ Progress saved but XP could not be awarded");
      }
    } catch (error) {
      console.error("❌ Stage completion error:", error);
      setLog("❌ Error saving progress");
    }
  };

  const loadSolution = () => {
    if (!progress[currentLevel][currentStageIndex] && !codeIsCorrect) {
      setShowSkipWarning(true);
      return;
    }

    setCode(currentStage.solution);
    setLog("💡 Solution loaded. Study it and try to understand!");
  };

  const forceLoadSolution = () => {
    setShowSkipWarning(false);
    setCode(currentStage.solution);
    setLog("💡 Solution loaded. Study it and try to understand!");
  };

  const nextStage = () => {
    const totalStages = LESSONS[currentLevel].stages.length;
    if (currentStageIndex < totalStages - 1) {
      setCurrentStageIndex(currentStageIndex + 1);
      setCodeIsCorrect(false);
    } else {
      if (currentLevel === "beginner" && isLevelUnlocked("intermediate")) {
        setCurrentLevel("intermediate");
        setCurrentStageIndex(0);
        setCodeIsCorrect(false);
      } else if (currentLevel === "intermediate" && isLevelUnlocked("expert")) {
        setCurrentLevel("expert");
        setCurrentStageIndex(0);
        setCodeIsCorrect(false);
      }
    }
  };

  const prevStage = () => {
    if (currentStageIndex > 0) {
      setCurrentStageIndex(currentStageIndex - 1);
    }
  };

  const getTotalProgress = () => {
    const total = Object.values(progress).flat().length;
    const completed = Object.values(progress).flat().filter(Boolean).length;
    return Math.round((completed / total) * 100);
  };

  // LearnPage fonksiyonunun en altındaki return kısmı:
  return (
    <>
      <Header></Header>
      <div className="learn-page-wrapper" style={{
        backgroundColor: "#050505",
        color: "#fff",
        height: "calc(100vh - 75px)",
        marginTop: "0",
        fontFamily: "'Segoe UI', Consolas, monospace",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflowX: "auto",
        overflowY: "hidden",
        padding: "20px"
      }}>

        <div style={{ position: "relative", zIndex: 1, display: "flex", flexDirection: "column", gap: "10px", width: "100%", alignItems: "center" }}>

          {/* HEADER / TOP BAR */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0 5px", height: "50px", width: "100%", maxWidth: "1600px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <div style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "40px",
                height: "40px",
                backgroundColor: "#111",
                border: "1px solid #333",
                borderRadius: "8px",
                boxShadow: "0 4px 10px rgba(0,0,0,0.5)"
              }}>
                <BookOpen size={24} color="#00d8ff" />
              </div>
              <div style={{ display: "flex", alignItems: "baseline", fontFamily: "'Segoe UI', sans-serif" }}>
                <span style={{ fontSize: "24px", fontWeight: "700", color: "#fff", letterSpacing: "-0.5px" }}>Sound</span>
                <span style={{ fontSize: "24px", fontWeight: "700", color: "#00d8ff" }}>Code</span>
                <span style={{ fontSize: "16px", fontWeight: "600", color: "#888", marginLeft: "10px" }}>Learn</span>
              </div>
            </div>

            <div style={{
              display: "flex",
              alignItems: "center",
              gap: "15px",
              padding: "10px 20px",
              background: "rgba(0, 216, 255, 0.05)",
              borderRadius: "8px",
              border: "1px solid rgba(0, 216, 255, 0.2)"
            }}>
              <Award size={20} color="#00d8ff" />
              <div>
                <div style={{ fontSize: "10px", color: "#666", fontWeight: "bold" }}>PROGRESS</div>
                <div style={{ fontSize: "18px", fontWeight: "bold", color: "#00d8ff" }}>{getTotalProgress()}%</div>
              </div>
            </div>
          </div>

          {/* --- ANA LAYOUT BURASI --- */}
          {/* --- ANA LAYOUT BURASI --- */}
          <div className="main-layout-container" style={{
            display: "flex",
            flexDirection: "row",
            flexWrap: "nowrap",
            minWidth: "1305px",
            ...(isMobile && {
              height: "auto",
              maxHeight: "50vh",
              minHeight: "400px"
            })
          }}>

            {/* 1. SOL PANEL: DERSLER (Güzel scrollbar eklendi) */}
            <div className="sidebar-lessons custom-scrollbar">
              {Object.entries(LESSONS).map(([levelKey, levelData]) => {
                const levelUnlocked = isLevelUnlocked(levelKey);

                return (
                  <div key={levelKey} style={{ marginBottom: "25px" }}>
                    <div style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      marginBottom: "12px",
                      opacity: levelUnlocked ? 1 : 0.4
                    }}>
                      <span style={{ fontSize: "16px" }}>{levelData.icon}</span>
                      <div>
                        <div style={{
                          fontSize: "11px",
                          fontWeight: "bold",
                          color: levelData.color,
                          letterSpacing: "1px"
                        }}>
                          {levelData.title}
                        </div>
                        <div style={{ fontSize: "9px", color: "#666" }}>
                          {levelData.subtitle}
                        </div>
                      </div>
                    </div>

                    {levelData.stages.map((stage, idx) => {
                      const isUnlocked = levelUnlocked && isStageUnlocked(levelKey, idx);
                      const isCompleted = progress[levelKey][idx];
                      const isCurrent = currentLevel === levelKey && currentStageIndex === idx;

                      return (
                        <div
                          key={stage.id}
                          onClick={() => {
                            if (isUnlocked) {
                              setCurrentLevel(levelKey);
                              setCurrentStageIndex(idx);
                            }
                          }}
                          className={`lesson-card ${!isUnlocked ? 'locked' : ''}`}
                          style={{
                            padding: "10px",
                            marginBottom: "6px",
                            background: isCurrent ? "rgba(0, 255, 0, 0.1)" : "rgba(255, 255, 255, 0.02)",
                            border: isCurrent ? `2px solid ${levelData.color}` : "1px solid #222",
                            borderRadius: "6px",
                            cursor: isUnlocked ? "pointer" : "not-allowed",
                            opacity: isUnlocked ? 1 : 0.3,
                            position: "relative"
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            {isCompleted ? (
                              <CheckCircle size={16} color={levelData.color} />
                            ) : isUnlocked ? (
                              <div style={{
                                width: "16px",
                                height: "16px",
                                border: "2px solid #555",
                                borderRadius: "50%"
                              }} />
                            ) : (
                              <Lock size={16} color="#555" />
                            )}

                            <div style={{ flex: 1 }}>
                              <div style={{
                                fontSize: "11px",
                                fontWeight: "bold",
                                color: isCurrent ? levelData.color : "#ccc"
                              }}>
                                {stage.title}
                              </div>
                              <div style={{
                                fontSize: "9px",
                                color: "#666",
                                marginTop: "2px"
                              }}>
                                {stage.description}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>

            {/* 2. ORTA PANEL: EDİTÖR */}
            <div className="editor-area">
              <div style={{
                padding: "15px 20px",
                backgroundColor: "#0a0a0a",
                borderBottom: "1px solid #222"
              }}>
                <div style={{
                  fontSize: "18px",
                  fontWeight: "bold",
                  color: LESSONS[currentLevel].color,
                  marginBottom: "5px"
                }}>
                  {currentStage.title}
                </div>

                <div style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  marginTop: "10px",
                  padding: "12px",
                  background: "rgba(0, 216, 255, 0.05)",
                  borderLeft: "3px solid #00d8ff",
                  borderRadius: "4px"
                }}>
                  <Target size={16} color="#00d8ff" />
                  <div style={{ fontSize: "12px", color: "#ccc" }}>
                    <strong style={{ color: "#00d8ff" }}>Goal:</strong> {currentStage.objective}
                  </div>
                </div>

                <div style={{ display: "flex", gap: "8px", marginTop: "10px" }}>
                  <button
                    onClick={() => setShowTheory(!showTheory)}
                    style={{
                      padding: "6px 12px",
                      fontSize: "11px",
                      background: showTheory ? "rgba(0, 100, 255, 0.2)" : "transparent",
                      color: showTheory ? "#09f" : "#666",
                      border: "1px solid " + (showTheory ? "#09f" : "#333"),
                      borderRadius: "4px",
                      cursor: "pointer"
                    }}
                  >
                    📖 Theory
                  </button>
                  <button
                    onClick={() => setShowHints(!showHints)}
                    style={{
                      padding: "6px 12px",
                      fontSize: "11px",
                      background: showHints ? "rgba(251, 146, 60, 0.2)" : "transparent",
                      color: showHints ? "#fb923c" : "#666",
                      border: "1px solid " + (showHints ? "#fb923c" : "#333"),
                      borderRadius: "4px",
                      cursor: "pointer"
                    }}
                  >
                    💡 Hints
                  </button>
                </div>

                {showTheory && (
                  <div style={{
                    marginTop: "10px",
                    padding: "12px",
                    background: "rgba(0, 100, 255, 0.05)",
                    border: "1px solid rgba(0, 100, 255, 0.2)",
                    borderRadius: "6px"
                  }}>
                    <div style={{ fontSize: "10px", fontWeight: "bold", color: "#09f", marginBottom: "5px" }}>THEORY</div>
                    <div style={{ fontSize: "11px", color: "#ccc", lineHeight: "1.5" }}>
                      {currentStage.theory}
                    </div>
                  </div>
                )}

                {showHints && (
                  <div style={{
                    marginTop: "10px",
                    padding: "12px",
                    background: "rgba(251, 146, 60, 0.05)",
                    border: "1px solid rgba(251, 146, 60, 0.2)",
                    borderRadius: "6px"
                  }}>
                    <div style={{ fontSize: "10px", fontWeight: "bold", color: "#fb923c", marginBottom: "5px" }}>HINTS</div>
                    {currentStage.hints.map((hint, idx) => (
                      <div key={idx} style={{
                        fontSize: "11px",
                        color: "#ccc",
                        marginBottom: "4px",
                        paddingLeft: "12px",
                        position: "relative"
                      }}>
                        <span style={{ position: "absolute", left: 0, color: "#fb923c" }}>•</span>
                        {hint}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div style={{ flex: 1, position: "relative", minHeight: 0 }}>
                <CodeMirror
                  value={code}
                  height="100%"
                  theme={oneDark}
                  extensions={[javascript()]}
                  onChange={(value) => setCode(value)}
                  onCreateEditor={onCreateEditor}
                  basicSetup={{ lineNumbers: true, highlightActiveLine: false }}
                  style={{ height: "100%" }}
                />
              </div>
            </div>

            {/* 3. SAĞ PANEL: KONTROLLER (Scrollbar GİZLENDİ) */}
            <div className="sidebar-controls hide-scrollbar">

              <div style={{
                padding: "15px",
                background: "#0a0a0a",
                border: "1px solid #222",
                borderRadius: "8px",
                marginBottom: "15px"
              }}>
                <div style={{ fontSize: "9px", color: "#555", fontWeight: "bold", letterSpacing: "1px", marginBottom: "5px" }}>
                  CURRENT STAGE
                </div>
                <div style={{ fontSize: "16px", color: LESSONS[currentLevel].color, fontWeight: "bold" }}>
                  {currentStageIndex + 1} / {LESSONS[currentLevel].stages.length}
                </div>
                <div style={{ fontSize: "10px", color: "#666", marginTop: "5px" }}>
                  {LESSONS[currentLevel].title}
                </div>
              </div>

              <div style={{
                padding: "15px",
                background: "#0a0a0a",
                border: "1px solid #222",
                borderRadius: "8px",
                marginBottom: "15px"
              }}>
                <button
                  onClick={runCode}
                  disabled={isPlaying || !isLoaded}
                  style={{
                    width: "100%",
                    padding: "12px",
                    fontSize: "14px",
                    fontWeight: "bold",
                    backgroundColor: !isLoaded ? "#333" : (isPlaying ? "#222" : "#00d8ff"),
                    color: !isLoaded ? "#888" : (isPlaying ? "#555" : "#000"),
                    border: "none",
                    borderRadius: "6px",
                    cursor: (!isLoaded || isPlaying) ? "default" : "pointer",
                    boxShadow: (!isLoaded || isPlaying) ? "none" : "0 0 15px rgba(0,216,255,0.3)",
                    marginBottom: "8px"
                  }}
                >
                  {!isLoaded ? "LOADING..." : (isPlaying ? "RUNNING..." : "RUN CODE ▶")}
                </button>

                <button
                  onClick={stopMusic}
                  disabled={!isPlaying}
                  style={{
                    width: "100%",
                    padding: "12px",
                    fontSize: "14px",
                    fontWeight: "bold",
                    backgroundColor: !isPlaying ? "#111" : "#d00",
                    color: !isPlaying ? "#333" : "#fff",
                    border: !isPlaying ? "1px solid #222" : "none",
                    borderRadius: "6px",
                    cursor: !isPlaying ? "default" : "pointer"
                  }}
                >
                  STOP ■
                </button>
              </div>

              <div style={{
                padding: "15px",
                background: "#0a0a0a",
                border: "1px solid #222",
                borderRadius: "8px",
                marginBottom: "15px"
              }}>
                <button
                  onClick={loadSolution}
                  style={{
                    width: "100%",
                    padding: "10px",
                    fontSize: "12px",
                    background: "transparent",
                    color: "#fb923c",
                    border: "1px solid #fb923c",
                    borderRadius: "6px",
                    cursor: "pointer",
                    marginBottom: "8px"
                  }}
                >
                  💡 Show Solution
                </button>

                <button
                  onClick={completeStage}
                  disabled={progress[currentLevel][currentStageIndex] || !codeIsCorrect}
                  style={{
                    width: "100%",
                    padding: "10px",
                    fontSize: "12px",
                    background: progress[currentLevel][currentStageIndex] ? "#222" : (codeIsCorrect ? "transparent" : "#111"),
                    color: progress[currentLevel][currentStageIndex] ? "#666" : (codeIsCorrect ? "#c084fc" : "#444"),
                    border: "1px solid " + (progress[currentLevel][currentStageIndex] ? "#444" : (codeIsCorrect ? "#c084fc" : "#333")),
                    borderRadius: "6px",
                    cursor: (progress[currentLevel][currentStageIndex] || !codeIsCorrect) ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "6px"
                  }}
                >
                  {progress[currentLevel][currentStageIndex] ? (
                    <>
                      <CheckCircle size={14} />
                      Completed
                    </>
                  ) : codeIsCorrect ? (
                    <>
                      <Trophy size={14} />
                      Mark Complete
                    </>
                  ) : (
                    <>
                      <Lock size={14} />
                      Solve First
                    </>
                  )}
                </button>
              </div>

              <div style={{
                display: "flex",
                gap: "8px",
                marginBottom: "15px"
              }}>
                <button
                  onClick={prevStage}
                  disabled={currentLevel === "beginner" && currentStageIndex === 0}
                  style={{
                    flex: 1,
                    padding: "10px",
                    fontSize: "12px",
                    background: "#222",
                    color: "#ccc",
                    border: "1px solid #555",
                    borderRadius: "6px",
                    cursor: "pointer",
                    opacity: (currentLevel === "beginner" && currentStageIndex === 0) ? 0.3 : 1
                  }}
                >
                  ← Prev
                </button>

                <button
                  onClick={nextStage}
                  disabled={
                    (currentLevel === "expert" && currentStageIndex === LESSONS.expert.stages.length - 1) ||
                    !isStageUnlocked(currentLevel, currentStageIndex + 1)
                  }
                  style={{
                    flex: 1,
                    padding: "10px",
                    fontSize: "12px",
                    background: "#222",
                    color: "#ccc",
                    border: "1px solid #555",
                    borderRadius: "6px",
                    cursor: "pointer",
                    opacity: (currentLevel === "expert" && currentStageIndex === LESSONS.expert.stages.length - 1) ? 0.3 : 1
                  }}
                >
                  Next →
                </button>
              </div>

              <div style={{ flex: 1, minHeight: "100px" }}>
                <div className="custom-scrollbar" style={{
                  padding: "12px",
                  background: "#0a0a0a",
                  border: "1px solid #222",
                  borderRadius: "8px",
                  height: "100%",
                  overflowY: "auto"
                }}>
                  <div style={{ fontSize: "9px", color: "#555", fontWeight: "bold", letterSpacing: "1px", marginBottom: "8px" }}>
                    CONSOLE
                  </div>
                  <div style={{
                    fontSize: "10px",
                    color: "#ccc",
                    fontFamily: "monospace",
                    wordBreak: "break-word",
                    lineHeight: "1.4"
                  }}>
                    {log}
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* SKIP WARNING MODAL */}
        {showSkipWarning && (
          <div style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: "#1a1a1a",
              border: "2px solid #fb923c",
              borderRadius: "12px",
              padding: "40px",
              maxWidth: "500px",
              boxShadow: "0 20px 60px rgba(251, 146, 60, 0.3)"
            }}>
              <h3 style={{
                margin: "0 0 20px 0",
                fontSize: "24px",
                color: "#fb923c",
                display: "flex",
                alignItems: "center",
                gap: "10px"
              }}>
                ⚠️ Warning
              </h3>

              <p style={{
                margin: "0 0 30px 0",
                fontSize: "16px",
                color: "#ccc",
                lineHeight: "1.6"
              }}>
                You should try to solve it yourself before seeing the solution! If you see the solution now, your learning process will be shortened and you may struggle in later lessons.
                <br /><br />
                Do you still want to see it?
              </p>

              <div style={{
                display: "flex",
                gap: "15px"
              }}>
                <button
                  onClick={() => setShowSkipWarning(false)}
                  style={{
                    flex: 1,
                    padding: "15px",
                    fontSize: "16px",
                    fontWeight: "bold",
                    backgroundColor: "#c084fc",
                    color: "#000",
                    border: "none",
                    borderRadius: "8px",
                    cursor: "pointer"
                  }}
                >
                  ✓ No, I'll Solve It Myself
                </button>

                <button
                  onClick={forceLoadSolution}
                  style={{
                    flex: 1,
                    padding: "15px",
                    fontSize: "16px",
                    fontWeight: "bold",
                    backgroundColor: "transparent",
                    color: "#fb923c",
                    border: "2px solid #fb923c",
                    borderRadius: "8px",
                    cursor: "pointer"
                  }}
                >
                  → Yes, Show It Anyway
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* LOGIN REQUIRED MODAL */}
      {showLoginModal && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundColor: "rgba(0, 0, 0, 0.9)",
          backdropFilter: "blur(5px)",
          zIndex: 9999,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "'Segoe UI', sans-serif"
        }}>
          <div style={{
            backgroundColor: "#111",
            border: "2px solid #00d8ff",
            borderRadius: "16px",
            padding: "40px",
            width: "90%",
            maxWidth: "400px",
            textAlign: "center",
            boxShadow: "0 0 50px rgba(0, 216, 255, 0.3)"
          }}>
            <div style={{ marginBottom: "20px" }}>
              <Lock size={48} color="#00d8ff" />
            </div>

            <h2 style={{
              color: "#fff",
              fontSize: "24px",
              marginBottom: "10px",
              fontWeight: "bold"
            }}>
              Authentication Required
            </h2>

            <p style={{
              color: "#aaa",
              marginBottom: "30px",
              lineHeight: "1.5"
            }}>
              You must log in to use all features of SoundCode, access lessons, and save your progress.
            </p>

            <button
              onClick={() => window.location.href = "/signin"}
              style={{
                backgroundColor: "#00d8ff",
                color: "#111",
                border: "none",
                padding: "12px 30px",
                fontSize: "16px",
                fontWeight: "bold",
                borderRadius: "30px",
                cursor: "pointer",
                transition: "all 0.2s",
                boxShadow: "0 0 20px rgba(0, 216, 255, 0.4)"
              }}
              onMouseOver={(e) => {
                e.target.style.transform = "scale(1.05)";
                e.target.style.boxShadow = "0 0 30px rgba(0, 216, 255, 0.6)";
              }}
              onMouseOut={(e) => {
                e.target.style.transform = "scale(1)";
                e.target.style.boxShadow = "0 0 20px rgba(0, 216, 255, 0.4)";
              }}
            >
              Log In / Sign Up
            </button>
          </div>
        </div>
      )}
    </>
  );
}
export default LearnPage;
